import React, { useState, useCallback } from 'react';
import { todayIso, fmtDateMedium } from '../utils/dateUtils';
import { exportToPdf } from '../utils/exportToPdf';

const TODAY = todayIso();

// Format ISO date/datetime → DD-MMM-YYYY
const fmtDate = (v) => fmtDateMedium(v);

// For CSV export — plain date (use string slice to avoid UTC↔local shift)
const fmtDatePlain = (v) => {
  if (!v) return '';
  const s = String(v).trim();
  // If it's already a YYYY-MM-DD string (date-only from DB), return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // Otherwise parse and use UTC date parts to avoid timezone shift
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// ── Daily report column spec ──────────────────────────────────────────────────
// width = points; tuned so total fits A4 landscape (~778pt usable) at compact density.
// Columns that are the same for every guest
const COLS_DAILY_BASE = [
  { key: 'reservation_no',  label: 'Res No',     width: 45 },
  { key: 'arrival_date',    label: 'Arrival',    date: true, width: 42 },
  { key: 'departure_date',  label: 'Departure',  date: true, width: 42 },
  { key: 'guest_name',      label: 'Guest Name', width: 70 },
  { key: 'mobile_number',   label: 'Mobile',     width: 55 },
  { key: 'gender',          label: 'Gender',     width: 28 },
  { key: 'dob',             label: 'DOB',        date: true, width: 42 },
  { key: 'nationality',     label: 'Nationality',width: 45 },
  { key: 'address',         label: 'Address',    width: 80 },
  { key: 'purpose_of_visit',label: 'Purpose',    width: 45 },
];

// Extra columns for foreign guests (shown as additional columns in the unified table)
const COLS_FOREIGN_EXTRA = [
  { key: 'passport_number',        label: 'Passport No',      width: 52 },
  { key: 'visa_number',            label: 'Visa No',          width: 45 },
  { key: 'visa_expiry_date',       label: 'Visa Expiry',      date: true, width: 42 },
  { key: 'arrival_stamp_date',     label: 'Arrival Stamp',    date: true, width: 42 },
  { key: 'date_of_arrival_in_india', label: 'Arrival in India', date: true, width: 45 },
  { key: 'place_of_issue',         label: 'Place of Issue',   width: 50 },
  { key: 'cform_submission_status',label: 'C-Form',           width: 35 },
];

// ── Standalone FRRO / Foreign guest report columns ────────────────────────────
const COLUMNS_FOREIGN_REPORT = [
  { key: 'reservation_no',          label: 'Res No',           width: 45 },
  { key: 'arrival_date',            label: 'Arrival',          date: true, width: 42 },
  { key: 'departure_date',          label: 'Departure',        date: true, width: 42 },
  { key: 'guest_name',              label: 'Guest Name',       width: 70 },
  { key: 'mobile_number',           label: 'Mobile',           width: 55 },
  { key: 'nationality',             label: 'Nationality',      width: 50 },
  { key: 'gender',                  label: 'Gender',           width: 30 },
  { key: 'dob',                     label: 'DOB',              date: true, width: 42 },
  { key: 'passport_number',         label: 'Passport No',      width: 55 },
  { key: 'visa_number',             label: 'Visa No',          width: 48 },
  { key: 'visa_expiry_date',        label: 'Visa Expiry',      date: true, width: 42 },
  { key: 'arrival_stamp_date',      label: 'Arrival Stamp',    date: true, width: 42 },
  { key: 'date_of_arrival_in_india',label: 'Arrival in India', date: true, width: 45 },
  { key: 'place_of_issue',          label: 'Place of Issue',   width: 50 },
  { key: 'cform_submission_status', label: 'C-Form',           width: 35 },
  { key: 'address',                 label: 'Address',          width: 80 },
  { key: 'purpose_of_visit',        label: 'Purpose',          width: 45 },
];

// ── CSV export ────────────────────────────────────────────────────────────────
function buildDailyCSVColumns(rows) {
  const hasForeign = rows.some((r) => r.is_foreign_guest);
  const cols = [
    ...COLS_DAILY_BASE,
    { key: 'guest_type',    label: 'Guest Type', width: 40 },
    // For domestic
    { key: 'id_proof_type', label: 'ID Type',    width: 40 },
    { key: 'id_proof_no',   label: 'ID No',      width: 55 },
  ];
  if (hasForeign) cols.push(...COLS_FOREIGN_EXTRA);
  return cols;
}

function downloadPDF(rows, reportType, fromDate, toDate, propertyName, { print = false } = {}) {
  let colDefs, dataRows;
  if (reportType === 'foreign') {
    colDefs = COLUMNS_FOREIGN_REPORT;
    dataRows = rows;
  } else {
    colDefs = buildDailyCSVColumns(rows);
    dataRows = rows.map((r) => ({
      ...r,
      guest_type: r.is_foreign_guest ? 'Foreign' : 'Domestic',
    }));
  }
  const columns = colDefs.map((c) => ({ header: c.label, dataKey: c.key }));
  const tableRows = dataRows.map((row) => {
    const out = {};
    colDefs.forEach((c) => {
      let v = row[c.key];
      if (v === null || v === undefined || v === '') {
        out[c.key] = '-';
      } else if (c.date) {
        out[c.key] = fmtDatePlain(v);
      } else {
        out[c.key] = String(v);
      }
    });
    return out;
  });
  const reportTitle = reportType === 'foreign'
    ? 'Foreign Guests Report (FRRO / C-Form)'
    : 'Daily Arrivals Report — Police / Compliance';
  const subtitle = propertyName ? `${propertyName} — ${fromDate} to ${toDate}` : `${fromDate} to ${toDate}`;
  exportToPdf({
    title: reportTitle,
    subtitle,
    meta: [
      `Generated: ${new Date().toLocaleString()}`,
      `${tableRows.length} record${tableRows.length === 1 ? '' : 's'}`,
    ],
    sections: [{ columns, rows: tableRows }],
    // Police report has up to 20 columns when foreign guests are included
    // (10 base + Guest Type + ID Type + ID No + 7 foreign extras). The
    // column widths sum to ~820pt — over A4 landscape usable (770pt). On A4
    // jspdf-autotable squeezed them into a degenerate left strip with
    // headers rotated/stacked vertically. A3 landscape gives ~1119pt usable,
    // which fits the table cleanly while keeping compact font/padding.
    orientation: 'landscape',
    pageSize: 'a3',
    compact: true,
    print,
    filename: `PoliceReport_${reportType === 'foreign' ? 'FRRO' : 'Daily'}_${fromDate}_${toDate}.pdf`,
    footerLeft: propertyName || '',
  });
}

function downloadCSV(rows, reportType, fromDate, toDate) {
  let colDefs, dataRows;
  if (reportType === 'foreign') {
    colDefs = COLUMNS_FOREIGN_REPORT;
    dataRows = rows;
  } else {
    colDefs = buildDailyCSVColumns(rows);
    // Add computed fields
    dataRows = rows.map((r) => ({
      ...r,
      guest_type: r.is_foreign_guest ? 'Foreign' : 'Domestic',
    }));
  }

  const header = colDefs.map((c) => c.label).join(',');
  const lines = dataRows.map((row) =>
    colDefs.map((c) => {
      let v = row[c.key];
      if (v === null || v === undefined) v = '';
      if (c.date) v = fmtDatePlain(v);
      const str = String(v);
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"` : str;
    }).join(',')
  );
  const csv = [header, ...lines].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `PoliceReport_${reportType === 'foreign' ? 'FRRO' : 'Daily'}_${fromDate}_${toDate}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Cell value helper ─────────────────────────────────────────────────────────
function cellVal(row, col) {
  const v = row[col.key];
  if (v === null || v === undefined || v === '') return '-';
  if (col.date) return fmtDate(v);
  return String(v);
}

// ── Component ─────────────────────────────────────────────────────────────────
function PmsPoliceReport({ token, selectedProperty }) {
  const [reportType, setReportType] = useState('daily');
  const [fromDate, setFromDate] = useState(TODAY);
  const [toDate, setToDate] = useState(TODAY);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generated, setGenerated] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [scanModal, setScanModal] = useState(null); // { guestName, scanData, scanName } | null
  const [scanLoading, setScanLoading] = useState(false);

  const chainId = selectedProperty?.chain_id;
  const propertyId = selectedProperty?.id;
  const propertyName = selectedProperty?.name || '';

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId || '',
    'x-property-id': propertyId || '',
  };

  const generateReport = async () => {
    if (!propertyId) return setError('Please select a property first.');
    if (!fromDate || !toDate) return setError('Please select date range.');
    if (fromDate > toDate) return setError('From date must be before To date.');
    setError('');
    setLoading(true);
    setRows([]);
    setGenerated(false);
    try {
      const res = await fetch(
        `/api/v1/compliance/report?type=${reportType}&from=${fromDate}&to=${toDate}`,
        { headers: authHeaders }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to generate report');
      setRows(data.rows || []);
      setGenerated(true);
    } catch (err) {
      setError(err.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  // scanType: 'passport' | 'id'
  const viewScan = useCallback(async (row, scanType = 'passport') => {
    if (!row.guest_id) return;
    setScanLoading(true);
    setScanModal({ guestName: row.guest_name, scanData: null, scanName: null, loading: true });
    try {
      const res = await fetch(
        `/api/v1/pms/guests/${row.guest_id}/document-scan?type=${scanType}`,
        { headers: { Authorization: `Bearer ${token}`, 'x-chain-id': selectedProperty?.chain_id || '', 'x-property-id': selectedProperty?.id || '' } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Not found');
      setScanModal({ guestName: row.guest_name, scanData: data.scan_data, scanName: data.scan_name, loading: false });
    } catch (err) {
      setScanModal({ guestName: row.guest_name, scanData: null, scanName: null, loading: false, error: err.message });
    } finally {
      setScanLoading(false);
    }
  }, [token, selectedProperty]);

  const loadHistory = async () => {
    if (!propertyId) return;
    try {
      const res = await fetch('/api/v1/compliance/report/history', { headers: authHeaders });
      const data = await res.json();
      if (Array.isArray(data)) setHistory(data);
      setHistoryLoaded(true);
    } catch { /* ignore */ }
  };

  if (!propertyId) {
    return (
      <div style={{ padding: 32 }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>Police / Compliance Report</h2>
        <div style={{ padding: '12px 16px', background: '#fffbeb', border: '1px solid #fbbf24',
          borderRadius: 8, color: '#b45309', fontSize: 13 }}>
          Please select a property first from <strong>Hotel Config &gt; Select Property</strong>.
        </div>
      </div>
    );
  }

  const hasForeignInDaily = rows.some((r) => r.is_foreign_guest);

  return (
    <div style={{ padding: '1rem', fontFamily: 'inherit' }} className="police-report-container">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .police-report-container { padding: 0; }
          .print-header { display: block !important; }
          table { font-size: 9px; }
          th, td { padding: 3px 5px !important; }
        }
        .print-header { display: none; }
        .foreign-row { background: #eff6ff !important; }
        .domestic-row { background: #fff !important; }
        .foreign-row:nth-child(even) { background: #dbeafe !important; }
      `}</style>

      {/* ── Screen controls ── */}
      <div className="no-print">
        <h3 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Front Office — Police / Compliance Report</h3>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>{propertyName}</div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end',
          padding: '12px 16px', background: '#f8faff', border: '1px solid #d7e3f4', borderRadius: 8, marginBottom: 16 }}>
          <div>
            <label style={ctrlLabel}>Report Type</label>
            <select value={reportType} onChange={(e) => { setReportType(e.target.value); setGenerated(false); setRows([]); }}
              style={ctrlInput}>
              <option value="daily">Daily Arrivals (All Guests)</option>
              <option value="foreign">Foreign Guests (FRRO / C-Form)</option>
            </select>
          </div>
          <div>
            <label style={ctrlLabel}>From Date</label>
            <input type="date" value={fromDate} onChange={(e) => { const v = e.target.value; setFromDate(v); if (toDate && v && toDate < v) setToDate(v); }} style={ctrlInput} />
          </div>
          <div>
            <label style={ctrlLabel}>To Date</label>
            <input type="date" value={toDate} onChange={(e) => { const v = e.target.value; setToDate(v); if (fromDate && v && fromDate > v) setFromDate(v); }} min={fromDate} style={ctrlInput} />
          </div>
          <button onClick={generateReport} disabled={loading} style={btnPrimary}>
            {loading ? 'Generating…' : 'Generate Report'}
          </button>
          {generated && rows.length > 0 && (
            <>
              <button onClick={() => downloadCSV(rows, reportType, fromDate, toDate)} style={btnGreen}>
                Download CSV
              </button>
              <button
                onClick={() => downloadPDF(rows, reportType, fromDate, toDate, propertyName, { print: false })}
                style={btnPurple}
                title="Save PDF directly to your Downloads folder"
              >
                Download PDF
              </button>
              <button
                onClick={() => downloadPDF(rows, reportType, fromDate, toDate, propertyName, { print: true })}
                style={btnIndigo}
                title="Open PDF in a new tab with the print dialog ready"
              >
                Print PDF
              </button>
            </>
          )}
          <button onClick={loadHistory} style={btnOutline}>Report History</button>
        </div>

        {error && (
          <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5',
            borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{error}</div>
        )}

        {historyLoaded && history.length > 0 && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#f9fafb',
            border: '1px solid #e5e7eb', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Report History (last 50)</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f3f4f6' }}>
                  {['Params', 'Generated By', 'Date/Time'].map((h) => (
                    <th key={h} style={hth}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.log_id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={htd}>{h.report_params}</td>
                    <td style={htd}>{h.modified_by}</td>
                    <td style={htd}>{h.modified_on ? new Date(h.modified_on).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Print header ── */}
      <div className="print-header" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{propertyName}</div>
        <div style={{ fontSize: 13 }}>
          {reportType === 'foreign' ? 'Foreign Guest Report (FRRO / C-Form)' : 'Daily Arrivals Report'}
          {' '}&mdash; {fmtDate(fromDate)} to {fmtDate(toDate)}
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
          Generated: {new Date().toLocaleString()}
        </div>
      </div>

      {/* ── Results ── */}
      {generated && (
        <div>
          <div className="no-print" style={{ fontSize: 13, color: 'var(--theme-text)', marginBottom: 8, display: 'flex', gap: 16, alignItems: 'center' }}>
            <span><b>{rows.length}</b> record{rows.length !== 1 ? 's' : ''} — {fmtDate(fromDate)} → {fmtDate(toDate)}</span>
            {reportType === 'daily' && hasForeignInDaily && (
              <span style={{ fontSize: 11, padding: '2px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: 4, fontWeight: 600 }}>
                Blue rows = Foreign guests with passport details
              </span>
            )}
          </div>

          {rows.length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: 13 }}>No records found for the selected date range.</div>
          ) : reportType === 'foreign' ? (
            // ── FRRO / Foreign-only table ──
            <ForeignTable rows={rows} onViewScan={viewScan} />

          ) : (
            // ── Daily arrivals — unified domestic + foreign ──
            <DailyTable rows={rows} hasForeign={hasForeignInDaily} onViewScan={viewScan} />
          )}
        </div>
      )}

      {/* ── Document Scan Modal ── */}
      {scanModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={() => setScanModal(null)}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 20, maxWidth: 640, width: '95vw', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--theme-primary)' }}>
                Document Scan — {scanModal.guestName}
              </div>
              <button onClick={() => setScanModal(null)} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            {scanModal.loading && <div style={{ color: '#6b7280', textAlign: 'center', padding: 20 }}>Loading scan...</div>}
            {scanModal.error && <div style={{ color: '#991b1b', padding: 10 }}>Error: {scanModal.error}</div>}
            {!scanModal.loading && scanModal.scanData && (() => {
              // ID scans are a multi-file slot (front+back, multiple pages) and
              // the document-scan endpoint returns arrays for that type;
              // passport stays a single value. Normalize to a list either way
              // so this renders identically for both.
              const dataList = Array.isArray(scanModal.scanData) ? scanModal.scanData : [scanModal.scanData];
              const nameList = Array.isArray(scanModal.scanName) ? scanModal.scanName : [scanModal.scanName];
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {dataList.map((src, idx) => {
                    const name = nameList[idx] || '';
                    const isImage = src.startsWith('data:image') || /\.(jpg|jpeg|png|gif|webp|bmp)(\?|$)/i.test(name) || /\.(jpg|jpeg|png|gif|webp|bmp)(\?|$)/i.test(src);
                    const isPdf = src.startsWith('data:application/pdf') || /\.pdf(\?|$)/i.test(name) || /\.pdf(\?|$)/i.test(src);
                    return (
                      <div key={idx}>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>{name}</div>
                        {isImage ? (
                          <div>
                            <img src={src} alt="Document scan"
                              style={{ maxWidth: '100%', borderRadius: 6, border: '1px solid #e5e7eb' }}
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline-block'; }} />
                            <a href={src} target="_blank" rel="noreferrer" download={name}
                              style={{ display: 'none', padding: '8px 16px', background: 'var(--theme-primary)', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
                              Open / Download Document
                            </a>
                          </div>
                        ) : isPdf ? (
                          <div>
                            <div style={{ marginBottom: 8, color: 'var(--theme-text)', fontSize: 13 }}>PDF Document: {name}</div>
                            <a href={src} download={name} target="_blank" rel="noreferrer"
                              style={{ padding: '8px 16px', background: 'var(--theme-primary)', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
                              Download PDF
                            </a>
                          </div>
                        ) : (
                          // URL path — try rendering as image first, fallback to download link
                          <div>
                            <img src={src} alt="Document scan"
                              style={{ maxWidth: '100%', borderRadius: 6, border: '1px solid #e5e7eb' }}
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                            <a href={src} target="_blank" rel="noreferrer" download={name}
                              style={{ display: 'none', padding: '8px 16px', background: 'var(--theme-primary)', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
                              Open / Download Document
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Daily Arrivals table (domestic + foreign in one view) ─────────────────────
function DailyTable({ rows, hasForeign, onViewScan }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
            <th style={th}>#</th>
            {COLS_DAILY_BASE.map((c) => <th key={c.key} style={th}>{c.label}</th>)}
            {/* Document columns — header changes by row, so we use two column headers */}
            <th style={th}>Doc Type</th>
            <th style={th}>Doc No</th>
            <th style={th}>Scan</th>
            {hasForeign && (
              <>
                <th style={{ ...th, background: '#1e40af' }}>Visa No</th>
                <th style={{ ...th, background: '#1e40af' }}>Visa Expiry</th>
                <th style={{ ...th, background: '#1e40af' }}>Arrival Stamp</th>
                <th style={{ ...th, background: '#1e40af' }}>Arrival in India</th>
                <th style={{ ...th, background: '#1e40af' }}>Place of Issue</th>
                <th style={{ ...th, background: '#1e40af' }}>C-Form</th>
              </>
            )}
            <th style={th}>Rooms</th>
            <th style={th}>Pax</th>
            <th style={th}>Source</th>
            <th style={th}>Status</th>
            <th style={th}>Compliance</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const isForeign = Boolean(row.is_foreign_guest);
            const isOverridden = Boolean(row.override_reason);
            const rowStyle = {
              borderBottom: '1px solid #e5e7eb',
              background: isOverridden
                ? (i % 2 === 0 ? '#fffbeb' : '#fef3c7')
                : isForeign
                  ? (i % 2 === 0 ? '#eff6ff' : '#dbeafe')
                  : (i % 2 === 0 ? '#fff' : '#f9fafb'),
            };
            return (
              <tr key={i} style={rowStyle}>
                <td style={td}>{i + 1}</td>
                {COLS_DAILY_BASE.map((c) => (
                  <td key={c.key} style={td}>{cellVal(row, c)}</td>
                ))}
                {/* Doc Type */}
                <td style={td}>
                  {isForeign
                    ? <span style={foreignBadge}>Passport</span>
                    : (row.id_proof_type || '-')}
                </td>
                {/* Doc No */}
                <td style={td}>
                  {isForeign ? (row.passport_number || '-') : (row.id_proof_no || '-')}
                </td>
                {/* Scan */}
                <td style={td}>
                  {isForeign && row.has_scan ? (
                    <button onClick={() => onViewScan(row, 'passport')}
                      style={{ fontSize: 11, padding: '2px 8px', background: '#1e40af', color: '#fff',
                        border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
                      View
                    </button>
                  ) : !isForeign && row.has_id_scan ? (
                    <button onClick={() => onViewScan(row, 'id')}
                      style={{ fontSize: 11, padding: '2px 8px', background: '#047857', color: '#fff',
                        border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
                      View ID
                    </button>
                  ) : (
                    <span style={{ fontSize: 10, color: '#9ca3af' }}>—</span>
                  )}
                </td>
                {hasForeign && (
                  <>
                    <td style={td}>{isForeign ? (row.visa_number || '-') : ''}</td>
                    <td style={td}>{isForeign ? fmtDate(row.visa_expiry_date) : ''}</td>
                    <td style={td}>{isForeign ? fmtDate(row.arrival_stamp_date) : ''}</td>
                    <td style={td}>{isForeign ? fmtDate(row.date_of_arrival_in_india) : ''}</td>
                    <td style={td}>{isForeign ? (row.place_of_issue || '-') : ''}</td>
                    <td style={td}>{isForeign ? (row.cform_submission_status || '-') : ''}</td>
                  </>
                )}
                <td style={td}>{row.room_count || '-'}</td>
                <td style={td}>{row.total_pax || '-'}</td>
                <td style={td}>{row.booking_source || '-'}</td>
                <td style={{ ...td, fontWeight: 600, color: row.status === 'CHECKED_IN' ? '#166534' : 'var(--theme-text)' }}>
                  {row.status || '-'}
                </td>
                <td style={td}>
                  {isOverridden ? (
                    <span
                      title={`Manager Override${row.override_by ? ` by ${row.override_by}` : ''}: ${row.override_reason}`}
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: 10,
                        fontWeight: 700,
                        background: '#f59e0b',
                        color: '#fff',
                      }}
                    >OVERRIDE</span>
                  ) : (
                    <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 600 }}>OK</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── FRRO Foreign Guest table ──────────────────────────────────────────────────
function ForeignTable({ rows, onViewScan }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#1e40af', color: '#fff' }}>
            <th style={th}>#</th>
            {COLUMNS_FOREIGN_REPORT.map((c) => <th key={c.key} style={th}>{c.label}</th>)}
            <th style={th}>Passport Scan</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #e5e7eb', background: i % 2 === 0 ? '#eff6ff' : '#dbeafe' }}>
              <td style={td}>{i + 1}</td>
              {COLUMNS_FOREIGN_REPORT.map((c) => (
                <td key={c.key} style={td}>{cellVal(row, c)}</td>
              ))}
              <td style={td}>
                {row.has_scan ? (
                  <button onClick={() => onViewScan(row, 'passport')}
                    style={{ fontSize: 11, padding: '2px 8px', background: '#1e40af', color: '#fff',
                      border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
                    View
                  </button>
                ) : (
                  <span style={{ fontSize: 10, color: '#9ca3af' }}>—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const th = { padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap', fontWeight: 600, fontSize: 11 };
const td = { padding: '5px 8px', verticalAlign: 'top', fontSize: 12 };
const hth = { padding: '6px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700 };
const htd = { padding: '5px 10px', fontSize: 12 };
const foreignBadge = { fontSize: 10, padding: '1px 6px', background: '#1e40af', color: '#fff', borderRadius: 4, fontWeight: 700 };

const ctrlLabel = { fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 };
const ctrlInput = { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 };
const btnPrimary = { padding: '8px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 600, cursor: 'pointer', fontSize: 13 };
const btnGreen   = { padding: '8px 16px', background: 'var(--theme-success)', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 600, cursor: 'pointer', fontSize: 13 };
const btnPurple  = { padding: '8px 16px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 600, cursor: 'pointer', fontSize: 13 };
const btnIndigo  = { padding: '8px 16px', background: '#4338ca', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 600, cursor: 'pointer', fontSize: 13 };
const btnOutline = { padding: '8px 14px', background: '#fff', color: 'var(--theme-text)', border: '1px solid #d1d5db', borderRadius: 7, cursor: 'pointer', fontSize: 13 };

export default PmsPoliceReport;
